import React, { Component } from 'react';
import T from 'prop-types';
// components
import { FormattedMessage } from 'react-intl';
import Button from 'components/Button';
import Modal from 'components/Modal';
import Input from 'components/Input';
import AsyncSelectLoadMore from 'components/AsyncSelect/AsyncSelectLoadMore';
import ButtonRadioGroup from 'components/ButtonRadioGroup/ButtonRadioGroup';
// utils
import classnames from 'classnames/bind';
import { formatAssetLink, capitalize } from 'utils';
import { toastResponseErrors } from 'utils/responseErrorsHelper';
import get from 'lodash.get';
// api
import { createLinkAsset, updateLinkAsset, updateAssetName } from 'endpoints/tenantResources';
import { searchFarms } from 'endpoints/farms';
// assets
import styles from './AddTenantAssetModal.module.scss';

const cn = classnames.bind(styles);
const modes = ['initial', 'create_link', 'edit_link', 'rename'];

const farmAssignmentOptions = [
  { value: true, label: <FormattedMessage id="general.allFarms" /> },
  { value: false, label: <FormattedMessage id="general.specificFarm" /> },
];

const getFarmOptions = (params) => searchFarms({ ...params, active: true });

class AddTenantAssetModal extends Component {

  state = {
    mode: this.props.initialMode,
    link: this.props.initialLink || '',
    name: this.props.initialName || '',
    isAllFarms: !this.props.initialFarm,
    farm: this.props.initialFarm
      ? { value: this.props.initialFarm.id, label: this.props.initialFarm.name }
      : null,
    linkErrorText: '',
    isLoading: false,
  };

  componentDidMount() {
    const { onBrowseLinkAssign } = this.props;
    if (document && onBrowseLinkAssign) {
      const browseLink = document.getElementById('add-file-modal');
      onBrowseLinkAssign(browseLink);
    }
  }

  getModalTitle = (mode) => {
    switch (mode) {
      case 'initial':
        return <FormattedMessage id="component.addResourceModal.new" />;
      case 'create_link':
        return <FormattedMessage id="component.addResourceModal.createLink" />;
      case 'edit_link':
        return <FormattedMessage id="component.addResourceModal.editLink" />;
      case 'rename':
        return <FormattedMessage id="general.editName" />;
      default:
        return null;
    }
  };

  onLinkChange = ({ target: { value } }) => {
    this.setState({ link: value });
  };

  onNameChange = ({ target: { value } }) => {
    this.setState({ name: value });
  };

  onFarmAssignmentChange = ({ target: { value } }) => {
    const isAllFarms = value === 'true';
    this.setState(
      (prevState) => ({ isAllFarms, farm: isAllFarms ? null : prevState.farm }),
      this.notifyUploadFarmChange
    );
  };

  onFarmChange = (option) => {
    this.setState({ farm: option || null }, this.notifyUploadFarmChange);
  };

  notifyUploadFarmChange = () => {
    const { onUploadFarmChange } = this.props;
    if (onUploadFarmChange) onUploadFarmChange(this.getFarmId());
  };

  getFarmId = () => {
    const { isAllFarms, farm } = this.state;
    return !isAllFarms && farm ? farm.value : null;
  };

  errorCallback = (response) => {
    const linkErrorText = get(response, 'errors.link[0]');
    if (linkErrorText) {
      this.setState({ linkErrorText, isLoading: false });
      return;
    }
    this.setState({ isLoading: false });
    toastResponseErrors(response);
  };

  onLinkAssetCreate = () => {
    const { link, name } = this.state;
    const { onLinkCreate } = this.props;
    const formattedLink = formatAssetLink(link);
    this.setState({ isLoading: true });
    createLinkAsset(formattedLink, name, this.getFarmId())
      .then((resource) => {
        onLinkCreate(resource);
        this.closeModal();
      })
      .catch(this.errorCallback);
  };

  onLinkAssetUpdate = () => {
    const { onLinkUpdate, assetId } = this.props;
    const { link, name } = this.state;
    const formattedLink = formatAssetLink(link);
    this.setState({ isLoading: true });
    updateLinkAsset(assetId, formattedLink, name, this.getFarmId())
      .then((resource) => {
        onLinkUpdate(resource);
        this.closeModal();
      })
      .catch(this.errorCallback);
  };

  onNameAssetUpdate = () => {
    const { onFileNameUpdate, assetId } = this.props;
    const { name } = this.state;
    this.setState({ isLoading: true });
    updateAssetName(assetId, name, this.getFarmId())
      .then((resource) => {
        onFileNameUpdate(resource);
        this.closeModal();
      }).catch(this.errorCallback);
  };

  // () => onClose() instead of onClose because of event goes to redux action payload
  closeModal = () => this.props.onClose();

  isSaveDisabled = () => {
    const { mode, link, name, isLoading, isAllFarms, farm } = this.state;
    const { initialLink, initialName, initialFarm } = this.props;
    const isFarmMissed = !isAllFarms && !farm;

    if (mode === 'create_link') return !link || !name || isFarmMissed || isLoading;

    const isSameName = (initialName === name);
    const isSameLink = (initialLink === link);
    const isSameFarm = ((initialFarm ? initialFarm.id : null) === this.getFarmId());

    if (mode === 'rename') return isLoading || !name || isFarmMissed || (isSameName && isSameFarm);
    return isLoading || !name || !link || isFarmMissed || (isSameName && isSameLink && isSameFarm);
  };

  renderFarmAssignment = () => {
    const { isAllFarms, farm } = this.state;
    return (
      <div className={cn('farm-assignment')}>
        <div className={cn('farm-label')}>
          <FormattedMessage id="component.addResourceModal.visibleTo" />
        </div>
        <ButtonRadioGroup
          name="farm-assignment"
          options={farmAssignmentOptions}
          value={isAllFarms}
          onChange={this.onFarmAssignmentChange}
        />
        {!isAllFarms && (
          <div className={cn('farm-select')}>
            <AsyncSelectLoadMore
              isClearable
              selected={farm || {}}
              onChange={this.onFarmChange}
              optionsGetter={getFarmOptions}
              placeholder={<FormattedMessage id="general.selectFarm" />}
              loadMoreMode="auto"
              autoLoad
            />
          </div>
        )}
      </div>
    );
  };

  render() {
    const { mode, link, linkErrorText, name, isAllFarms, farm } = this.state;
    const title = this.getModalTitle(mode);
    const isSaveDisabled = this.isSaveDisabled();
    const isFarmMissed = !isAllFarms && !farm;
    return (
      <Modal title={title} onClose={this.closeModal}>
        <section className="modal-body">
          {(mode === 'initial') && (
            <>
              {this.renderFarmAssignment()}
              <div className={cn('add-resource-body')}>
                <div id="add-file-modal" className={cn('add-resource-square', { disabled: isFarmMissed })}>
                  <i className={cn('fa fa-ep-doc', 'icon')} />
                  <FormattedMessage id="general.file" />
                </div>
                <div
                  className={cn('add-resource-square', { disabled: isFarmMissed })}
                  onClick={() => !isFarmMissed && this.setState({ mode: 'create_link' })}
                >
                  <i className={cn('fa fa-link', 'icon')} />
                  <FormattedMessage id="general.link" />
                </div>
              </div>
            </>
          )}
          {(mode !== 'initial') && (
            <div className={cn('url-input-body')}>
              <div className={cn('url-label')}>Name</div>
              <Input
                value={name}
                onChange={this.onNameChange}
                placeholder="Name"
                hasNoMargin
              />
              {['create_link', 'edit_link'].includes(mode) && (
                <>
                  <div className={cn('url-label')}>URL</div>
                  <Input
                    value={link}
                    onChange={this.onLinkChange}
                    placeholder="www.example.com"
                    hasNoMargin
                  />
                  {linkErrorText && link && (
                    <div className={cn('error-text')}>{capitalize(linkErrorText)}</div>
                  )}
                </>
              )}
              {this.renderFarmAssignment()}
            </div>
          )}
        </section>
        {(mode !== 'initial') && (
          <section className="modal-footer text-right">
            <Button className="margin-right-1" onClick={this.closeModal}>
              <FormattedMessage id="general.button.cancel" />
            </Button>
            {(mode === 'create_link') && (
              <Button primary disabled={isSaveDisabled} onClick={this.onLinkAssetCreate}>
                <FormattedMessage id="general.button.create" />
              </Button>
            )}
            {(mode !== 'create_link') && (
              <Button
                primary
                disabled={isSaveDisabled}
                onClick={mode === 'edit_link' ? this.onLinkAssetUpdate : this.onNameAssetUpdate}
              >
                <FormattedMessage id="general.button.save" />
              </Button>
            )}
          </section>
        )}
      </Modal>
    );
  }
}

AddTenantAssetModal.propTypes = {
  initialLink: T.string,
  initialName: T.string,
  initialFarm: T.shape({
    id: T.number,
    name: T.string,
  }),
  assetId: T.number,
  onClose: T.func.isRequired,
  onLinkCreate: T.func,
  onLinkUpdate: T.func,
  onFileNameUpdate: T.func,
  onBrowseLinkAssign: T.func,
  onUploadFarmChange: T.func,
  initialMode: T.oneOf(modes),
};

AddTenantAssetModal.defaultProps = {
  initialMode: 'initial',
};

export default AddTenantAssetModal;
